import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IdCard as IdCardIcon } from 'lucide-react';
import { onboardingApi } from '../../api/onboarding';
import { probationApi } from '../../api/probation';
import { employeeApi, idCardApi, employmentHistoryApi } from '../../api/hr';
import { contractApi, contractTemplateApi } from '../../api/contracts';
import { leaveApi } from '../../api/leave';
import { trainingApi } from '../../api/training';
import { performanceApi } from '../../api/performance';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Trash2, Send, FileSearch, Check, X, PenTool, CheckCircle2, LogOut, MinusCircle, RotateCcw } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { Employee } from '../../types/hr';
import { DocumentsSection } from '../../components/DocumentsSection';
import { employeeDocumentApi } from '../../api/documents';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { CommentsThread } from '../../components/CommentsThread';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { timesheetApi } from '../../api/payroll';
import { usePermission } from '../../auth/AuthContext';
import { identificationApi, qualificationApi, compensationHistoryApi, projectAssignmentApi } from '../../api/staff-master-record';
import { projectsApi } from '../../api/projects';

const TABS = ['Overview', 'Employment', 'Master Record', 'Onboarding', 'Probation', 'Leave', 'Training', 'Performance', 'Documents'] as const;
type Tab = typeof TABS[number];

/**
 * Real tabbed workspace, matching the reference design - previously
 * one long scrolling page with Leave/Training/Performance completely
 * absent (confirmed via inspection: the data layer already supported
 * per-employee lookups, they were simply never exposed as endpoints
 * or shown here). Same underlying data and mutations as before, just
 * genuinely reorganised into one connected workspace.
 */
export function StaffDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const id = Number(employeeId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const canDeleteTimesheets = usePermission('timesheets.delete');

  const employeeQuery = useQuery({ queryKey: ['hr-employee', id], queryFn: () => employeeApi.getById(id) });
  const completionQuery = useQuery({ queryKey: ['hr-employee-completion', id], queryFn: () => employeeApi.getProfileCompletion(id) });
  const documentsQuery = useQuery({ queryKey: ['staff-documents', id], queryFn: () => employeeDocumentApi.listForEmployee(id) });
  const cardQuery = useQuery({ queryKey: ['hr-id-card', id], queryFn: () => idCardApi.getForEmployee(id) });
  const historyQuery = useQuery({ queryKey: ['hr-employment-history', id], queryFn: () => employmentHistoryApi.list(id) });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list() });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list() });
  const timesheetsQuery = useQuery({ queryKey: ['staff-timesheets', id], queryFn: () => timesheetApi.listForEmployee(id) });
  const deleteTimesheetMutation = useMutation({
    mutationFn: (timesheetId: number) => timesheetApi.delete(timesheetId),
    onSuccess: () => { showSuccess('Timesheet deleted.'); queryClient.invalidateQueries({ queryKey: ['staff-timesheets', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete timesheet.'),
  });
  const identificationQuery = useQuery({ queryKey: ['staff-identification', id], queryFn: () => identificationApi.get(id), enabled: activeTab === 'Master Record' });
  const qualificationsQuery = useQuery({ queryKey: ['staff-qualifications', id], queryFn: () => qualificationApi.listForEmployee(id), enabled: activeTab === 'Master Record' });
  const compensationHistoryQuery = useQuery({ queryKey: ['staff-compensation-history', id], queryFn: () => compensationHistoryApi.list(id), enabled: activeTab === 'Master Record' });
  const projectAssignmentsQuery = useQuery({ queryKey: ['staff-project-assignments', id], queryFn: () => projectAssignmentApi.listForEmployee(id), enabled: activeTab === 'Master Record' });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: activeTab === 'Master Record' });
  const leaveQuery = useQuery({ queryKey: ['staff-leave', id], queryFn: () => leaveApi.listForEmployee(id), enabled: activeTab === 'Leave' || activeTab === 'Overview' });
  const trainingQuery = useQuery({ queryKey: ['staff-training', id], queryFn: () => trainingApi.listForEmployee(id), enabled: activeTab === 'Training' || activeTab === 'Overview' });
  const performanceQuery = useQuery({ queryKey: ['staff-performance', id], queryFn: () => performanceApi.listForEmployee(id), enabled: activeTab === 'Performance' || activeTab === 'Overview' });

  const { register, handleSubmit } = useForm<Partial<Employee>>({ values: employeeQuery.data?.data });

  const saveEmploymentMutation = useMutation({
    mutationFn: (values: Partial<Employee>) =>
      employeeApi.updateEmployment(id, {
        grade: values.grade, employmentType: values.employment_type, employmentStatus: values.employment_status,
        departmentId: values.department_id ? Number(values.department_id) : null, positionId: values.position_id ? Number(values.position_id) : null,
      }),
    onSuccess: () => { showSuccess('Employment details saved.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); queryClient.invalidateQueries({ queryKey: ['hr-employee-completion', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save employment details.'),
  });

  const generateStaffIdMutation = useMutation({
    mutationFn: () => employeeApi.generateStaffId(id),
    onSuccess: (result) => { showSuccess(`Staff ID generated - ${result.data.staffId}`); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const [salaryDraft, setSalaryDraft] = useState('');
  const salaryMutation = useMutation({
    mutationFn: (amount: string) => employeeApi.updateSalary(id, { baseSalary: amount ? Number(amount) : null }),
    onSuccess: () => { showSuccess('Salary updated.'); queryClient.invalidateQueries({ queryKey: ['hr-employee', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update salary.'),
  });

  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const generateCardMutation = useMutation({
    mutationFn: () => idCardApi.generate(id, { issueDate: issueDate || undefined, expiryDate: expiryDate || undefined }),
    onSuccess: () => { showSuccess('ID card generated.'); queryClient.invalidateQueries({ queryKey: ['hr-id-card', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to generate ID card.'),
  });

  const deactivateCardMutation = useMutation({
    mutationFn: (cardId: number) => idCardApi.deactivate(id, cardId),
    onSuccess: () => { showSuccess('ID card deactivated.'); queryClient.invalidateQueries({ queryKey: ['hr-id-card', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate ID card.'),
  });

  if (!employeeQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const employee = employeeQuery.data.data;
  const nextOfKin = employeeQuery.data.contacts.filter((c) => c.contact_type === 'next_of_kin');
  const emergencyContacts = employeeQuery.data.contacts.filter((c) => c.contact_type !== 'next_of_kin');
  const completion = completionQuery.data?.data;

  return (
    <div className="page">
      <Link to="/hr/staff">&larr; Back to Staff Management</Link>

      <div className="staff-workspace-header">
        <div>
          <h1>{employee.full_name}</h1>
          <p className="muted">
            {employee.staff_id ?? 'No Staff ID assigned'} · {employee.official_email}
            {!employee.staff_id && (
              <button className="link-button" onClick={() => generateStaffIdMutation.mutate()} disabled={generateStaffIdMutation.isPending}>
                {generateStaffIdMutation.isPending ? 'Generating…' : 'Generate Staff ID'}
              </button>
            )}
          </p>
          <p className="muted">{employee.position_name ?? '-'} · {employee.department_name ?? '-'} · {employee.duty_station_name ?? '-'}</p>
        </div>
        {completion && (
          <div className="completion-ring-wrap">
            <CompletionRing percentage={completion.percentage} />
            <span className="muted">Profile: {completion.filled}/{completion.total} fields</span>
          </div>
        )}
      </div>

      <nav className="workspace-tabs">
        {TABS.map((tab) => (
          <button key={tab} className={'workspace-tab' + (activeTab === tab ? ' workspace-tab-active' : '')} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </nav>

      {activeTab === 'Overview' && (
        <>
          <section className="card">
            <h2>Emergency Contact</h2>
            {emergencyContacts.length === 0 && <p className="muted">None on file.</p>}
            <ul className="deadline-list">
              {emergencyContacts.map((c) => <li key={c.id}>{c.full_name} {c.relationship && `(${c.relationship})`} {c.phone && `· ${c.phone}`}</li>)}
            </ul>
          </section>
          <section className="card">
            <h2>Next of Kin</h2>
            {nextOfKin.length === 0 && <p className="muted">None on file.</p>}
            <ul className="deadline-list">
              {nextOfKin.map((c) => <li key={c.id}>{c.full_name} {c.relationship && `(${c.relationship})`} {c.phone && `· ${c.phone}`}</li>)}
            </ul>
          </section>
          <section className="card">
            <h2>Quick Summary</h2>
            <p className="muted">
              Leave requests: {leaveQuery.data?.data.length ?? '…'} · Training records: {trainingQuery.data?.data.length ?? '…'} · Appraisals: {performanceQuery.data?.data.length ?? '…'}
            </p>
          </section>
        </>
      )}

      {activeTab === 'Employment' && (
        <>
          <form onSubmit={handleSubmit((values) => saveEmploymentMutation.mutate(values))}>
            <section className="card">
              <h2>Employment</h2>
              <div className="project-header-grid">
                <StaticField label="Current Position" value={employee.position_name ?? '-'} />
                <StaticField label="Current Department" value={employee.department_name ?? '-'} />
                <StaticField label="Duty Station" value={employee.duty_station_name ?? '-'} />
                <StaticField label="Supervisor" value={employee.supervisor_name ?? '-'} />
                <StaticField label="Start Date" value={employee.start_date ?? '-'} />
              </div>
              <label>
                Department
                <select {...register('department_id')}>
                  <option value="">- not set -</option>
                  {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label>
                Position
                <select {...register('position_id')}>
                  <option value="">- not set -</option>
                  {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </label>
              <label>Grade<input {...register('grade')} /></label>
              <label>
                Employment Type
                <select {...register('employment_type')}>
                  <option value="">- not set -</option>
                  <option value="full_time">Full Time</option><option value="part_time">Part Time</option>
                  <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
                </select>
              </label>
              <label>
                Employment Status
                <select {...register('employment_status')}>
                  <option value="active">Active</option><option value="on_leave">On Leave</option>
                  <option value="probation">Probation</option><option value="suspended">Suspended</option><option value="separated">Separated</option>
                </select>
              </label>
              <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveEmploymentMutation.isPending}>{saveEmploymentMutation.isPending ? 'Saving…' : 'Save'}</button></div>
            </section>
          </form>

          <section className="card">
            <h2><IdCardIcon size={16} className="inline-icon" /> ID Card</h2>
            {cardQuery.data?.data ? (
              <>
                <p>Status: <strong>{cardQuery.data.data.status.replace('_', ' ')}</strong> · Issued: {cardQuery.data.data.issue_date ?? '-'} · Expires: {cardQuery.data.data.expiry_date ?? 'N/A'}</p>
                {cardQuery.data.data.status === 'active' && (
                  <button className="button-danger" onClick={() => deactivateCardMutation.mutate(cardQuery.data!.data!.id)} disabled={deactivateCardMutation.isPending}>Deactivate Card</button>
                )}
              </>
            ) : (
              <p className="muted">No ID card issued yet.</p>
            )}
            <div className="inline-form">
              <label>Issue Date<input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></label>
              <label>Expiry Date<input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></label>
              <button onClick={() => generateCardMutation.mutate()} disabled={!employee.staff_id || generateCardMutation.isPending}>
                {generateCardMutation.isPending ? 'Generating…' : 'Generate New Card'}
              </button>
            </div>
            {!employee.staff_id && <p className="muted">This employee has no Staff ID yet - a card cannot be generated.</p>}
          </section>

          <section className="card">
            <h2>Employment History</h2>
            {historyQuery.data && (
              <DataTable
                rows={historyQuery.data.data}
                rowKey={(h) => h.id}
                emptyMessage="No transfers, promotions, or amendments recorded yet."
                columns={[
                  { header: 'Date', render: (h) => h.effective_date },
                  { header: 'Type', render: (h) => h.change_type.replace('_', ' ') },
                  { header: 'Approved By', render: (h) => h.approved_by_name ?? '-' },
                  { header: 'Notes', render: (h) => h.notes ?? '-' },
                ]}
              />
            )}
          </section>

          <ContractsSection employeeId={id} />

          <section className="card">
            <h2>Payroll</h2>
            {timesheetsQuery.data && (
              <DataTable
                rows={timesheetsQuery.data.data}
                rowKey={(t) => t.id}
                emptyMessage="No timesheets recorded yet."
                columns={[
                  { header: 'Month', render: (t) => t.period_month },
                  { header: 'Days Worked', render: (t) => t.days_worked ?? '-' },
                  { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
                  { header: '', render: (t) => canDeleteTimesheets && t.status === 'draft' && <IconActionButton icon={Trash2} label="Delete" danger onClick={() => { if (window.confirm('Delete this timesheet?')) deleteTimesheetMutation.mutate(t.id); }} /> },
                ]}
              />
            )}
            <p className="muted">Bank: {employee.bank_name ?? '-'} · Account: {employee.bank_account_number ?? '-'}</p>
            <div className="inline-form">
              <label>
                Salary Override
                <input type="number" placeholder={employee.base_salary ? undefined : 'Using grade default'} defaultValue={employee.base_salary ?? ''} onChange={(e) => setSalaryDraft(e.target.value)} />
              </label>
              <button onClick={() => salaryMutation.mutate(salaryDraft)} disabled={salaryMutation.isPending}>Save Salary</button>
            </div>
          </section>
        </>
      )}

      {activeTab === 'Leave' && (
        <section className="card">
          <h2>Leave History</h2>
          {leaveQuery.data && (
            <DataTable
              rows={leaveQuery.data.data}
              rowKey={(l) => l.id}
              emptyMessage="No leave requests yet."
              columns={[
                { header: 'Type', render: (l) => l.leave_type_name ?? '-' },
                { header: 'Dates', render: (l) => `${l.start_date} - ${l.end_date}` },
                { header: 'Status', render: (l) => <StatusBadge status={l.status} /> },
              ]}
            />
          )}
        </section>
      )}

      {activeTab === 'Training' && (
        <section className="card">
          <h2>Training History</h2>
          {trainingQuery.data && (
            <DataTable
              rows={trainingQuery.data.data}
              rowKey={(t) => t.id}
              emptyMessage="No training records yet."
              columns={[
                { header: 'Title', render: (t) => t.catalogue_title ?? t.training_title ?? '-' },
                { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
                { header: 'Completed', render: (t) => t.completed_at ?? '-' },
              ]}
            />
          )}
        </section>
      )}

      {activeTab === 'Performance' && (
        <section className="card">
          <h2>Performance History</h2>
          {performanceQuery.data && (
            <DataTable
              rows={performanceQuery.data.data}
              rowKey={(p) => p.id}
              emptyMessage="No appraisals yet."
              columns={[
                { header: 'Cycle', render: (p) => p.cycle_name },
                { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
                { header: 'Final Rating', render: (p) => p.final_rating ?? '-' },
              ]}
            />
          )}
        </section>
      )}

      {activeTab === 'Master Record' && (
        <MasterRecordTab
          employeeId={id}
          identification={identificationQuery.data?.data ?? null}
          qualifications={qualificationsQuery.data?.data ?? []}
          compensationHistory={compensationHistoryQuery.data?.data ?? []}
          projectAssignments={projectAssignmentsQuery.data?.data ?? []}
          projects={projectsQuery.data?.data ?? []}
        />
      )}

      {activeTab === 'Onboarding' && <OnboardingTab employeeId={id} />}

      {activeTab === 'Probation' && <ProbationTab employeeId={id} />}

      {activeTab === 'Documents' && documentsQuery.data && (
        <DocumentsSection
          documents={documentsQuery.data.data}
          downloadUrl={(documentId) => employeeDocumentApi.downloadForEmployeeUrl(id, documentId)}
          upload={(input) => employeeDocumentApi.uploadForEmployee(id, input)}
          onDelete={(documentId) => employeeDocumentApi.deleteForEmployee(id, documentId)}
          queryKeyToInvalidate={['staff-documents', id]}
        />
      )}

      <CommentsThread entityType="employee" entityId={id} />
    </div>
  );
}

function CompletionRing({ percentage }: { percentage: number }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <svg width="70" height="70" viewBox="0 0 70 70">
      <circle cx="35" cy="35" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="6" />
      <circle cx="35" cy="35" r={radius} fill="none" stroke="#15803d" strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 35 35)" />
      <text x="35" y="40" textAnchor="middle" fontSize="16" fontWeight="700" fill="#15803d">{percentage}%</text>
    </svg>
  );
}

function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', hr_review: 'HR Review / Pending Approval', returned: 'Returned for Redraft', approved: 'Approved',
  signed: 'Signed', finalised: 'Finalised', expired: 'Expired', terminated: 'Terminated', rejected: 'Rejected',
};

function ContractsSection({ employeeId }: { employeeId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const contractsQuery = useQuery({ queryKey: ['contracts', employeeId], queryFn: () => contractApi.listForEmployee(employeeId) });
  const templatesQuery = useQuery({ queryKey: ['contract-templates'], queryFn: () => contractTemplateApi.list(), enabled: showForm });
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contracts', employeeId] });

  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => contractApi.create(employeeId, { templateId: values.templateId ? Number(values.templateId) : null, startDate: values.startDate, endDate: values.endDate || null }),
    onSuccess: (result) => { showSuccess(`Contract created - ${result.data.contractReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create contract.'),
  });

  const submitMutation = useMutation({ mutationFn: (id: number) => contractApi.submit(id), onSuccess: () => { showSuccess('Submitted for HR review.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const requestApprovalMutation = useMutation({ mutationFn: (id: number) => contractApi.requestApproval(id), onSuccess: () => { showSuccess('Approval requested.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decideMutation = useMutation({ mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => contractApi.decide(id, { decision }), onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const signMutation = useMutation({ mutationFn: (id: number) => contractApi.sign(id), onSuccess: () => { showSuccess('Marked as signed.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const finaliseMutation = useMutation({ mutationFn: (id: number) => contractApi.finalise(id), onSuccess: () => { showSuccess('Contract finalised - employee record updated.'); invalidate(); queryClient.invalidateQueries({ queryKey: ['hr-employee', employeeId] }); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <section className="card">
      <div className="page-header"><h2>Contracts</h2><button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Contract'}</button></div>

      {showForm && (
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Template
            <select {...register('templateId')}>
              <option value="">- no template -</option>
              {templatesQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
          <label>End Date<input type="date" {...register('endDate')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button>
        </form>
      )}

      {contractsQuery.data && (
        <DataTable
          rows={contractsQuery.data.data}
          rowKey={(c) => c.id}
          emptyMessage="No contracts yet."
          columns={[
            { header: 'Reference', render: (c) => c.contract_reference },
            { header: 'Start', render: (c) => c.start_date },
            { header: 'End', render: (c) => c.end_date ?? 'Open-ended' },
            { header: 'Status', render: (c) => <StatusBadge status={c.status} label={CONTRACT_STATUS_LABELS[c.status]} /> },
            {
              header: '',
              render: (c) => (
                <span className="row-actions">
                  {c.status === 'draft' && <IconActionButton icon={Send} label="Submit" onClick={() => submitMutation.mutate(c.id)} />}
                  {c.status === 'hr_review' && !c.approval_requested && <IconActionButton icon={FileSearch} label="Request Approval" onClick={() => requestApprovalMutation.mutate(c.id)} />}
                  {c.status === 'hr_review' && c.approval_requested && (
                    <>
                      <IconActionButton icon={Check} label="Approve" onClick={() => decideMutation.mutate({ id: c.id, decision: 'approved' })} />
                      <IconActionButton icon={X} label="Reject" danger onClick={() => decideMutation.mutate({ id: c.id, decision: 'rejected' })} />
                      <ReturnAction onReturn={(comment) => contractApi.return(c.id, comment)} queryKeysToInvalidate={[['contracts', employeeId]]} />
                    </>
                  )}
                  {c.status === 'approved' && <IconActionButton icon={PenTool} label="Mark Signed" onClick={() => signMutation.mutate(c.id)} />}
                  {c.status === 'signed' && <IconActionButton icon={CheckCircle2} label="Finalise" onClick={() => finaliseMutation.mutate(c.id)} />}
                  {c.generated_content && <a href={contractApi.downloadUrl(c.id)} target="_blank" rel="noreferrer">Download</a>}
                </span>
              ),
            },
          ]}
        />
      )}
      {contractsQuery.data?.data.filter((c) => c.status === 'returned').map((c) => (
        <ReturnedBanner key={c.id} returnComment={c.return_comment} resubmit={() => contractApi.resubmit(c.id)} queryKeysToInvalidate={[['contracts', employeeId]]} />
      ))}
    </section>
  );
}

const QUALIFICATION_TYPE_LABELS: Record<string, string> = { degree: 'Degree', certification: 'Certification', membership: 'Professional Membership', language: 'Language' };

function MasterRecordTab({
  employeeId, identification, qualifications, compensationHistory, projectAssignments, projects,
}: {
  employeeId: number;
  identification: import('../../api/staff-master-record').EmployeeIdentification | null;
  qualifications: import('../../api/staff-master-record').EmployeeQualification[];
  compensationHistory: import('../../api/staff-master-record').CompensationHistoryEntry[];
  projectAssignments: import('../../api/staff-master-record').ProjectAssignment[];
  projects: import('../../types/projects').Project[];
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // ---- Identification ----
  const idForm = useForm<Record<string, string>>({ values: identification ? Object.fromEntries(Object.entries(identification).map(([k, v]) => [k, v ?? ''])) : undefined });
  const saveIdMutation = useMutation({
    mutationFn: (values: Record<string, string>) => identificationApi.upsert(employeeId, values),
    onSuccess: () => { showSuccess('Identification details saved.'); queryClient.invalidateQueries({ queryKey: ['staff-identification', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  // ---- Qualifications ----
  const qualForm = useForm<{ qualificationType: string; title: string; institution?: string; fieldOfStudy?: string; yearObtained?: string }>();
  const addQualMutation = useMutation({
    mutationFn: (v: { qualificationType: string; title: string; institution?: string; fieldOfStudy?: string; yearObtained?: string }) =>
      qualificationApi.createForEmployee(employeeId, { ...v, yearObtained: v.yearObtained ? Number(v.yearObtained) : undefined }),
    onSuccess: () => { showSuccess('Qualification added.'); queryClient.invalidateQueries({ queryKey: ['staff-qualifications', employeeId] }); qualForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add.'),
  });
  const deleteQualMutation = useMutation({
    mutationFn: (qualId: number) => qualificationApi.deleteForEmployee(employeeId, qualId),
    onSuccess: () => { showSuccess('Qualification removed.'); queryClient.invalidateQueries({ queryKey: ['staff-qualifications', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove.'),
  });

  // ---- Project Assignments ----
  const assignForm = useForm<{ projectId: string; roleOnProject?: string; allocationPercentage?: string; startDate: string }>();
  const assignMutation = useMutation({
    mutationFn: (v: { projectId: string; roleOnProject?: string; allocationPercentage?: string; startDate: string }) =>
      projectAssignmentApi.create(employeeId, { projectId: Number(v.projectId), roleOnProject: v.roleOnProject, allocationPercentage: v.allocationPercentage ? Number(v.allocationPercentage) : undefined, startDate: v.startDate }),
    onSuccess: () => { showSuccess('Project assignment added.'); queryClient.invalidateQueries({ queryKey: ['staff-project-assignments', employeeId] }); assignForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add.'),
  });
  const endAssignMutation = useMutation({
    mutationFn: (assignId: number) => projectAssignmentApi.end(assignId, new Date().toISOString().slice(0, 10)),
    onSuccess: () => { showSuccess('Assignment ended.'); queryClient.invalidateQueries({ queryKey: ['staff-project-assignments', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to end.'),
  });

  return (
    <>
      <section className="card">
        <h2>Identification</h2>
        <p className="muted">Restricted access - visible only to HR Manager and Admin.</p>
        <form onSubmit={idForm.handleSubmit((v) => saveIdMutation.mutate(v))}>
          <label>National ID Type<input {...idForm.register('national_id_type')} /></label>
          <label>National ID Number<input {...idForm.register('national_id_number')} /></label>
          <label>Passport Number<input {...idForm.register('passport_number')} /></label>
          <label>Passport Expiry<input type="date" {...idForm.register('passport_expiry_date')} /></label>
          <label>Driver's Licence Number<input {...idForm.register('drivers_licence_number')} /></label>
          <label>Driver's Licence Expiry<input type="date" {...idForm.register('drivers_licence_expiry_date')} /></label>
          <label>Other Identification<textarea rows={2} {...idForm.register('other_identification')} /></label>
          <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveIdMutation.isPending}>{saveIdMutation.isPending ? 'Saving…' : 'Save'}</button></div>
        </form>
      </section>

      <section className="card">
        <h2>Qualifications</h2>
        <form className="inline-form" onSubmit={qualForm.handleSubmit((v) => addQualMutation.mutate(v))}>
          <label>
            Type
            <select {...qualForm.register('qualificationType', { required: true })}>
              <option value="degree">Degree</option><option value="certification">Certification</option>
              <option value="membership">Professional Membership</option><option value="language">Language</option>
            </select>
          </label>
          <label>Title<input {...qualForm.register('title', { required: true })} /></label>
          <label>Institution<input {...qualForm.register('institution')} /></label>
          <label>Field of Study<input {...qualForm.register('fieldOfStudy')} /></label>
          <label>Year<input type="number" {...qualForm.register('yearObtained')} /></label>
          <button type="submit" disabled={addQualMutation.isPending}>Add</button>
        </form>
        <DataTable
          rows={qualifications}
          rowKey={(q) => q.id}
          emptyMessage="No qualifications recorded yet."
          columns={[
            { header: 'Type', render: (q) => QUALIFICATION_TYPE_LABELS[q.qualification_type] },
            { header: 'Title', render: (q) => q.title },
            { header: 'Institution', render: (q) => q.institution ?? '-' },
            { header: 'Year', render: (q) => q.year_obtained ?? '-' },
            { header: '', render: (q) => <IconActionButton icon={Trash2} label="Remove" danger onClick={() => deleteQualMutation.mutate(q.id)} /> },
          ]}
        />
      </section>

      <section className="card">
        <h2>Compensation History</h2>
        <p className="muted">A permanent record - never edited, only appended to whenever salary changes.</p>
        <DataTable
          rows={compensationHistory}
          rowKey={(c) => c.id}
          emptyMessage="No compensation changes recorded yet."
          columns={[
            { header: 'Effective Date', render: (c) => c.effective_date },
            { header: 'Previous', render: (c) => c.previous_base_salary ? `${c.currency} ${Number(c.previous_base_salary).toLocaleString()}` : '-' },
            { header: 'New', render: (c) => `${c.currency} ${Number(c.new_base_salary).toLocaleString()}` },
            { header: 'Reason', render: (c) => c.reason ?? '-' },
            { header: 'Changed By', render: (c) => c.changed_by_name },
          ]}
        />
      </section>

      <section className="card">
        <h2>Project / Programme Assignments</h2>
        <form className="inline-form" onSubmit={assignForm.handleSubmit((v) => assignMutation.mutate(v))}>
          <label>
            Project
            <select {...assignForm.register('projectId', { required: true })}>
              <option value="">- select -</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </label>
          <label>Role<input {...assignForm.register('roleOnProject')} /></label>
          <label>Allocation %<input type="number" {...assignForm.register('allocationPercentage')} /></label>
          <label>Start Date<input type="date" {...assignForm.register('startDate', { required: true })} /></label>
          <button type="submit" disabled={assignMutation.isPending}>Assign</button>
        </form>
        <DataTable
          rows={projectAssignments}
          rowKey={(a) => a.id}
          emptyMessage="Not assigned to any project yet."
          columns={[
            { header: 'Project', render: (a) => a.project_name },
            { header: 'Role', render: (a) => a.role_on_project ?? '-' },
            { header: 'Allocation', render: (a) => a.allocation_percentage ? `${a.allocation_percentage}%` : '-' },
            { header: 'Status', render: (a) => a.is_active ? 'Active' : `Ended ${a.end_date}` },
            { header: '', render: (a) => a.is_active ? <IconActionButton icon={LogOut} label="End Assignment" onClick={() => endAssignMutation.mutate(a.id)} /> : null },
          ]}
        />
      </section>
    </>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  hr_documentation: 'HR Documentation', contract: 'Contract', identification_verification: 'Identification Verification',
  bank_payroll: 'Bank/Payroll', it_account_setup: 'IT/Account Setup', equipment_assets: 'Equipment/Assets',
  orientation: 'Orientation', policies_conduct: 'Policies/Code of Conduct', safeguarding_psea: 'Safeguarding/PSEA',
  health_safety: 'Health and Safety', department_induction: 'Department Induction', supervisor_responsibilities: 'Supervisor Responsibilities',
};

function OnboardingTab({ employeeId }: { employeeId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const onboardingQuery = useQuery({ queryKey: ['staff-onboarding', employeeId], queryFn: () => onboardingApi.getForEmployee(employeeId) });

  const startMutation = useMutation({
    mutationFn: () => onboardingApi.createForEmployee(employeeId),
    onSuccess: () => { showSuccess('Onboarding checklist started with the default task set.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to start onboarding.'),
  });

  const completeTaskMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: number; status: 'completed' | 'not_applicable' }) => onboardingApi.completeTask(taskId, { status }),
    onSuccess: () => { showSuccess('Task updated.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const reopenMutation = useMutation({
    mutationFn: (taskId: number) => onboardingApi.reopenTask(taskId),
    onSuccess: () => { showSuccess('Task reopened.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const completeChecklistMutation = useMutation({
    mutationFn: () => onboardingApi.completeChecklist(onboardingQuery.data!.data!.id),
    onSuccess: () => { showSuccess('Onboarding marked complete.'); queryClient.invalidateQueries({ queryKey: ['staff-onboarding', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to complete onboarding - check every task is resolved.'),
  });

  if (!onboardingQuery.data) return <div className="card"><p className="muted">Loading…</p></div>;

  if (!onboardingQuery.data.data) {
    return (
      <section className="card">
        <h2>Onboarding</h2>
        <p className="muted">No onboarding checklist started yet.</p>
        <button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>{startMutation.isPending ? 'Starting…' : 'Start Onboarding'}</button>
      </section>
    );
  }

  const checklist = onboardingQuery.data.data;
  const tasks = onboardingQuery.data.tasks;
  const allResolved = tasks.every((t) => t.status !== 'pending');

  return (
    <section className="card">
      <div className="page-header">
        <h2>Onboarding - {checklist.status === 'completed' ? 'Completed' : 'In Progress'}</h2>
        {checklist.status === 'in_progress' && (
          <button onClick={() => completeChecklistMutation.mutate()} disabled={!allResolved || completeChecklistMutation.isPending}>
            {completeChecklistMutation.isPending ? 'Completing…' : 'Mark Onboarding Complete'}
          </button>
        )}
      </div>
      <DataTable
        rows={tasks}
        rowKey={(t) => t.id}
        emptyMessage="No tasks."
        columns={[
          { header: 'Category', render: (t) => CATEGORY_LABELS[t.category] ?? t.category },
          { header: 'Task', render: (t) => t.task_name },
          { header: 'Responsible', render: (t) => t.responsible_name ?? '-' },
          { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
          {
            header: '',
            render: (t) => (
              <span className="row-actions">
                {t.status === 'pending' && (
                  <>
                    <IconActionButton icon={Check} label="Complete" onClick={() => completeTaskMutation.mutate({ taskId: t.id, status: 'completed' })} />
                    <IconActionButton icon={MinusCircle} label="Not Applicable" onClick={() => completeTaskMutation.mutate({ taskId: t.id, status: 'not_applicable' })} />
                  </>
                )}
                {t.status !== 'pending' && <IconActionButton icon={RotateCcw} label="Reopen" onClick={() => reopenMutation.mutate(t.id)} />}
              </span>
            ),
          },
        ]}
      />
    </section>
  );
}

const PROBATION_STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

function ProbationTab({ employeeId }: { employeeId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const probationQuery = useQuery({ queryKey: ['staff-probation', employeeId], queryFn: () => probationApi.getForEmployee(employeeId) });
  const current = probationQuery.data?.data[0];
  const detailQuery = useQuery({ queryKey: ['probation-detail', current?.id], queryFn: () => probationApi.getById(current!.id), enabled: !!current });

  const decisionForm = useForm<{ decision: string; comments: string; extensionNewEndDate?: string }>();
  const decisionMutation = useMutation({
    mutationFn: (v: { decision: string; comments: string; extensionNewEndDate?: string }) =>
      probationApi.recordHrDecision(current!.id, { decision: v.decision as 'confirmed' | 'extended' | 'non_confirmed', comments: v.comments, extensionNewEndDate: v.extensionNewEndDate }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['staff-probation', employeeId] }); queryClient.invalidateQueries({ queryKey: ['probation-detail', current?.id] }); queryClient.invalidateQueries({ queryKey: ['hr-employee', employeeId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision - the supervisor may not have submitted a recommendation yet.'),
  });

  if (!probationQuery.data) return <div className="card"><p className="muted">Loading…</p></div>;
  if (!current) return <section className="card"><h2>Probation</h2><p className="muted">No probation record for this employee. Start one from the Probation &amp; Confirmation page.</p></section>;

  return (
    <section className="card">
      <h2>Probation - {PROBATION_STATUS_LABELS[current.status] ?? current.status}</h2>
      <div className="project-header-grid">
        <StaticField label="Start Date" value={current.start_date} />
        <StaticField label="Expected End Date" value={current.expected_end_date} />
        <StaticField label="Supervisor" value={current.supervisor_name ?? '-'} />
      </div>

      {detailQuery.data && (
        <>
          <h3>Objectives</h3>
          <ul className="deadline-list">{detailQuery.data.objectives.map((o) => <li key={o.id}>{o.objective}</li>)}</ul>

          <h3>Reviews</h3>
          <ul className="deadline-list">
            {detailQuery.data.reviews.map((r) => <li key={r.id}>{r.review_date} - {r.assessment} ({r.submitted_by_name})</li>)}
          </ul>
        </>
      )}

      {current.supervisor_recommendation && (
        <p className="muted">Supervisor recommendation: <strong>{current.supervisor_recommendation.replace('_', ' ')}</strong> - {current.supervisor_recommendation_comments}</p>
      )}

      {current.status === 'under_review' && (
        <form onSubmit={decisionForm.handleSubmit((v) => decisionMutation.mutate(v))}>
          <h3>HR Decision</h3>
          <label>
            Decision
            <select {...decisionForm.register('decision', { required: true })}>
              <option value="">- select -</option>
              <option value="confirmed">Confirm</option>
              <option value="extended">Extend</option>
              <option value="non_confirmed">Do Not Confirm</option>
            </select>
          </label>
          <label>Comments<textarea rows={2} {...decisionForm.register('comments', { required: true })} /></label>
          <label>New End Date (if extending)<input type="date" {...decisionForm.register('extensionNewEndDate')} /></label>
          <div className="form-actions"><button type="submit" className="btn-primary" disabled={decisionMutation.isPending}>Record Decision</button></div>
        </form>
      )}

      {current.hr_decision && <p className="muted">HR decision: <strong>{current.hr_decision.replace('_', ' ')}</strong> - {current.hr_decision_comments}</p>}
    </section>
  );
}
